import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { BACKEND_NODE_URL } from '../config/constants';
import './GasDistributorPedidoModal.css';
import AppIcon from './icons/AppIcon';

export default function GasDistributorPedidoModal({ pedidoId, onClose }) {
  const [pedido, setPedido] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [editForm, setEditForm] = useState({
    endereco_entrega: '',
    horario_entrega: '',
    forma_pagamento: '',
    desconto_percentual: 0,
    observacoes_entrega: ''
  });
  const [editProdutos, setEditProdutos] = useState([]);

  useEffect(() => {
    if (pedidoId) {
      loadPedidoData();
    }
  }, [pedidoId]);

  const loadPedidoData = async () => {
    try {
      setLoading(true);

      // Buscar pedido completo
      const { data: pedidoData, error } = await supabase
        .from('pedido_gas_distributor')
        .select('*')
        .eq('id', pedidoId)
        .single();

      if (error) throw error;
      setPedido(pedidoData);
      setIsEditing(false);
      setFormError('');
      setEditForm({
        endereco_entrega: pedidoData?.endereco_entrega || '',
        horario_entrega: pedidoData?.horario_entrega || '',
        forma_pagamento: pedidoData?.forma_pagamento || '',
        desconto_percentual: pedidoData?.desconto_percentual || 0,
        observacoes_entrega: pedidoData?.observacoes_entrega || ''
      });

      // Parse produtos do novo campo
      if (pedidoData?.produtos) {
        let produtosArray = pedidoData.produtos;
        
        // Se for string, parsear
        if (typeof produtosArray === 'string') {
          try {
            produtosArray = JSON.parse(produtosArray);
          } catch (e) {
            console.error('Erro ao parsear produtos:', e);
            produtosArray = [];
          }
        }
        
        const normalizados = (produtosArray || []).map((p) => ({
          produto_id: p.produto_id,
          nome: p.nome || 'Produto',
          quantidade: p.quantidade || 1,
          preco_unitario: parseFloat(p.preco_unitario) || 0,
          subtotal: p.subtotal || (parseFloat(p.preco_unitario) || 0) * (p.quantidade || 1)
        }));
        setProdutos(normalizados);
        setEditProdutos(normalizados);
      } else {
        // Fallback para campos antigos (compatibilidade)
        const produtosArray = [];
        
        if (pedidoData?.produto_id) {
          const { data: produtoData } = await supabase
            .from('gas_distributor_products')
            .select('id, nome')
            .eq('id', pedidoData.produto_id)
            .single();
          
          produtosArray.push({
            produto_id: pedidoData.produto_id,
            nome: produtoData?.nome || 'Produto',
            quantidade: pedidoData.quantidade || 1,
            preco_unitario: parseFloat(pedidoData.preco_unitario) || 0,
            subtotal: (parseFloat(pedidoData.preco_unitario) || 0) * (pedidoData.quantidade || 1)
          });
        }
        
        // Produtos adicionais antigos
        if (pedidoData?.produtos_adicionais) {
          try {
            let adicionais = pedidoData.produtos_adicionais;
            if (typeof adicionais === 'string') {
              adicionais = JSON.parse(adicionais);
            }
            produtosArray.push(...(adicionais || []));
          } catch (e) {
            console.error('Erro ao parsear produtos adicionais:', e);
          }
        }
        
        setProdutos(produtosArray);
        setEditProdutos(produtosArray);
      }

    } catch (error) {
      console.error('Erro ao carregar pedido:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isStatusEditable = (status) =>
    ['Pendente', 'Confirmado', 'Agendado', 'Em aberto', 'Em Aberto'].includes(status);

  const getStatusClass = (status) => {
    const statusMap = {
      'Pendente': 'pendente',
      'Confirmado': 'confirmado',
      'Agendado': 'agendado',
      'Em Entrega': 'em-entrega',
      'Entregue': 'entregue',
      'Cancelado': 'cancelado'
    };
    return statusMap[status] || 'outro';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Pendente': 'schedule',
      'Confirmado': 'check_circle',
      'Agendado': 'event',
      'Em Entrega': 'local_shipping',
      'Entregue': 'task_alt',
      'Cancelado': 'cancel'
    };
    return icons[status] || 'info';
  };

  // Calcular subtotal dos produtos
  const calcularSubtotal = () => {
    if (produtos.length === 0) return 0;
    return produtos.reduce((sum, p) => sum + (p.subtotal || (p.preco_unitario * p.quantidade) || 0), 0);
  };

  // Calcular total de itens
  const calcularTotalItens = () => {
    if (produtos.length === 0) return 0;
    return produtos.reduce((sum, p) => sum + (p.quantidade || 0), 0);
  };

  const calcularSubtotalEdicao = () => {
    if (editProdutos.length === 0) return 0;
    return editProdutos.reduce((sum, p) => {
      const quantidade = parseInt(p.quantidade, 10) || 0;
      const preco = parseFloat(p.preco_unitario) || 0;
      return sum + (quantidade * preco);
    }, 0);
  };

  const totalEdicao = () => {
    const subtotalEdicao = calcularSubtotalEdicao();
    const desconto = Math.max(0, Math.min(100, parseFloat(editForm.desconto_percentual) || 0));
    return subtotalEdicao * (1 - desconto / 100);
  };

  const handleEditarProduto = (index, field, value) => {
    setEditProdutos((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSalvarEdicao = async () => {
    try {
      setSaving(true);
      setFormError('');

      const produtosPayload = (editProdutos || []).map((p) => {
        const quantidade = Math.max(1, parseInt(p.quantidade, 10) || 1);
        const preco = Math.max(0, parseFloat(p.preco_unitario) || 0);
        return {
          produto_id: p.produto_id,
          nome: p.nome || 'Produto',
          quantidade,
          preco_unitario: preco,
          subtotal: quantidade * preco
        };
      });

      if (produtosPayload.length === 0) {
        setFormError('Pedido precisa ter ao menos um produto.');
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        setFormError('Sessão expirada. Faça login novamente.');
        return;
      }

      const res = await fetch(`${BACKEND_NODE_URL}/api/gas-distributor/pedidos/${encodeURIComponent(pedidoId)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          produtos: produtosPayload,
          endereco_entrega: editForm.endereco_entrega,
          horario_entrega: editForm.horario_entrega,
          forma_pagamento: editForm.forma_pagamento,
          desconto_percentual: parseFloat(editForm.desconto_percentual) || 0,
          observacoes_entrega: editForm.observacoes_entrega || null
        })
      });

      const json = await res.json();
      if (!res.ok) {
        setFormError(json.error || 'Erro ao salvar pedido');
        return;
      }

      await loadPedidoData();
    } catch (err) {
      setFormError(err.message || 'Erro ao salvar pedido');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="pedido-modal-overlay" onClick={onClose}>
        <div className="pedido-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-loading">
            <AppIcon name="progress_activity" className="spinning" />
            <p>Carregando pedido...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!pedido) {
    return (
      <div className="pedido-modal-overlay" onClick={onClose}>
        <div className="pedido-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-error">
            <AppIcon name="error" />
            <p>Pedido não encontrado</p>
            <button onClick={onClose}>Fechar</button>
          </div>
        </div>
      </div>
    );
  }

  const subtotal = calcularSubtotal();
  const totalItens = calcularTotalItens();

  return (
    <div className="pedido-modal-overlay" onClick={onClose}>
      <div className="pedido-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="pedido-modal-header">
          <div className="header-left">
            <h2>Pedido #{pedido.id.slice(0, 8).toUpperCase()}</h2>
            <div className={`status-badge ${getStatusClass(pedido.status)}`}>
              <AppIcon name={getStatusIcon(pedido.status)} />
              {pedido.status}
            </div>
          </div>
          <button className="close-button" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        {/* Content */}
        <div className="pedido-modal-content">
          {formError && (
            <div className="section">
              <p className="observacoes-text" style={{ color: '#f87171' }}>{formError}</p>
            </div>
          )}

          {/* Produtos */}
          <div className="section">
            <h3>
              <AppIcon name="shopping_bag" />
              Produtos ({isEditing ? editProdutos.reduce((s, p) => s + (parseInt(p.quantidade, 10) || 0), 0) : totalItens} {totalItens === 1 ? 'item' : 'itens'})
            </h3>
            <div className="produtos-list">
              {(isEditing ? editProdutos : produtos).map((produto, index) => (
                <div key={index} className="produto-item">
                  <div className="produto-info">
                    <span className="produto-nome">{produto.nome || 'Produto'}</span>
                    {isEditing ? (
                      <input
                        type="number"
                        min="1"
                        value={produto.quantidade}
                        onChange={(e) => handleEditarProduto(index, 'quantidade', e.target.value)}
                        style={{ width: '72px' }}
                      />
                    ) : (
                      <span className="produto-qtd">{produto.quantidade}x</span>
                    )}
                  </div>
                  <div className="produto-precos">
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={produto.preco_unitario}
                        onChange={(e) => handleEditarProduto(index, 'preco_unitario', e.target.value)}
                        style={{ width: '110px' }}
                      />
                    ) : (
                      <span className="preco-unitario">{formatCurrency(produto.preco_unitario)} cada</span>
                    )}
                    <span className="subtotal">
                      {formatCurrency(isEditing
                        ? ((parseInt(produto.quantidade, 10) || 0) * (parseFloat(produto.preco_unitario) || 0))
                        : (produto.subtotal || (produto.preco_unitario * produto.quantidade)))}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Valores */}
          <div className="section valores-section">
            <h3>
              <AppIcon name="payments" />
              Valores
            </h3>
            <div className="valores-grid">
              <div className="valor-item">
                <span className="label">Subtotal</span>
                <span className="value">{formatCurrency(isEditing ? calcularSubtotalEdicao() : subtotal)}</span>
              </div>
              {(isEditing ? (parseFloat(editForm.desconto_percentual) || 0) : pedido.desconto_percentual) > 0 && (
                <div className="valor-item desconto">
                  <span className="label">
                    Desconto (
                    {isEditing ? (
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={editForm.desconto_percentual}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, desconto_percentual: e.target.value }))}
                        style={{ width: '70px', marginLeft: '6px' }}
                      />
                    ) : pedido.desconto_percentual}%
                    )
                  </span>
                  <span className="value">
                    -{formatCurrency((isEditing ? calcularSubtotalEdicao() : subtotal) * ((isEditing ? (parseFloat(editForm.desconto_percentual) || 0) : pedido.desconto_percentual) / 100))}
                  </span>
                </div>
              )}
              <div className="valor-item total">
                <span className="label">Total</span>
                <span className="value">{formatCurrency(isEditing ? totalEdicao() : pedido.preco_total)}</span>
              </div>
            </div>
          </div>

          {/* Entrega */}
          <div className="section">
            <h3>
              <AppIcon name="local_shipping" />
              Entrega
            </h3>
            <div className="info-grid">
              <div className="info-item">
                <AppIcon name="location_on" />
                <div className="info-content">
                  <span className="label">Endereço</span>
                  {isEditing ? (
                    <textarea
                      rows={2}
                      value={editForm.endereco_entrega}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, endereco_entrega: e.target.value }))}
                    />
                  ) : (
                    <span className="value">{pedido.endereco_entrega || 'Não informado'}</span>
                  )}
                </div>
              </div>
              <div className="info-item">
                <AppIcon name="schedule" />
                <div className="info-content">
                  <span className="label">Horário</span>
                  {isEditing ? (
                    <input
                      value={editForm.horario_entrega}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, horario_entrega: e.target.value }))}
                    />
                  ) : (
                    <span className="value">{pedido.horario_entrega || 'Não informado'}</span>
                  )}
                </div>
              </div>
              <div className="info-item">
                <AppIcon name="payments" />
                <div className="info-content">
                  <span className="label">Forma de Pagamento</span>
                  {isEditing ? (
                    <input
                      value={editForm.forma_pagamento}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, forma_pagamento: e.target.value }))}
                    />
                  ) : (
                    <span className="value">{pedido.forma_pagamento || 'Não informado'}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Observações */}
          {(pedido.observacoes_entrega || isEditing) && (
            <div className="section">
              <h3>
                <AppIcon name="note" />
                Observações
              </h3>
              {isEditing ? (
                <textarea
                  rows={3}
                  value={editForm.observacoes_entrega}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, observacoes_entrega: e.target.value }))}
                />
              ) : (
                <p className="observacoes-text">{pedido.observacoes_entrega}</p>
              )}
            </div>
          )}

          {/* Datas */}
          <div className="section datas-section">
            <div className="datas-grid">
              <div className="data-item">
                <AppIcon name="event_add" />
                <div className="data-content">
                  <span className="label">Criado em</span>
                  <span className="value">{formatDateTime(pedido.created_at)}</span>
                </div>
              </div>
              <div className="data-item">
                <AppIcon name="update" />
                <div className="data-content">
                  <span className="label">Atualizado em</span>
                  <span className="value">{formatDateTime(pedido.updated_at)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="pedido-modal-footer">
          {isStatusEditable(pedido.status) && !isEditing && (
            <button className="btn-secondary" onClick={() => setIsEditing(true)}>
              <AppIcon name="edit" />
              Editar pedido
            </button>
          )}
          {isEditing && (
            <>
              <button className="btn-secondary" onClick={() => setIsEditing(false)} disabled={saving}>
                <AppIcon name="undo" />
                Cancelar edição
              </button>
              <button className="btn-secondary" onClick={handleSalvarEdicao} disabled={saving}>
                <AppIcon name="save" />
                {saving ? 'Salvando...' : 'Salvar alterações'}
              </button>
            </>
          )}
          <button className="btn-secondary" onClick={onClose}>
            <AppIcon name="close" />
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
